/* Material Symbols variable subsets. From shopify/: rebuild common/core with tests/speed-icons-inventory.py --build-common/--build. */
(() => {
  'use strict';
  const script = document.querySelector('script[data-sugar-symbols]');
  if (!script || script.dataset.started) return;
  script.dataset.started = 'true';
  const CORE_NAMES = ["account_circle", "announcement", "arrow_forward", "arrow_left_alt", "arrow_right_alt", "auto_awesome", "autorenew", "badge", "bedtime", "block", "bolt", "calendar_month", "capture", "category", "check", "check_circle", "circle", "class", "clinical_notes", "close", "collapse_content", "currency_exchange", "dataset", "description", "east", "eco", "email", "error", "expand_content", "expand_more", "explore", "face_retouching_natural", "favorite", "filter_vintage", "fitness_center", "group", "handyman", "healing", "height", "help", "info", "lens", "light", "local_fire_department", "local_mall", "local_offer", "local_shipping", "lock", "mail", "menu", "menu_book", "message", "motion_photos_on", "navigation", "no_food", "nutrition", "package_2", "pan_tool", "percent", "percent_discount", "person", "play_arrow", "play_circle", "psychology", "radio_button_unchecked", "redeem", "refresh", "replay", "reviews", "schedule", "science", "search", "self_improvement", "sell", "send", "settings", "share", "shield", "shopping_bag", "shopping_cart", "spa", "star", "stars", "stat_1", "stat_2", "stat_3", "stethoscope", "straighten", "subscriptions", "support_agent", "thumb_up", "timeline", "timer", "title", "topic", "trending_up", "verified", "verified_user", "volume_off", "volume_up", "warning", "water_drop", "wb_sunny", "width"];
  const COMMON_NAMES = ["check_circle", "shopping_cart"];
  const common = new Set(COMMON_NAMES);
  const core = new Set(CORE_NAMES);
  const selector = '.material-symbols-outlined, .subscription-perks__icon--global, .quiz-answer__indicator--multi-checkmark';
  const nodes = new WeakMap();
  const waiting = new Map();
  const visibility = window.IntersectionObserver ? new IntersectionObserver(entries => {
    entries.forEach(({ target: node, isIntersecting }) => {
      const start = waiting.get(node);
      if (!start || !isIntersecting) return;
      if (!node.isConnected || !node.matches(selector)) { clear(node); return; }
      const state = nodes.get(node);
      state.visibilityBlocked = true;
      // The subscription adapter hides its host until ready. Test its parent's
      // visibility instead, but still test the host's layout/ancestor opacity.
      const perk = node.matches('.subscription-perks__icon--global');
      if (node.checkVisibility && !node.checkVisibility({ visibilityProperty: !perk, opacityProperty: true })) return;
      if ((perk || !node.checkVisibility) && /^(hidden|collapse)$/.test(getComputedStyle(perk ? node.parentElement : node).visibility)) return;
      if (!node.checkVisibility) {
        for (let ancestor = node; ancestor; ancestor = ancestor.parentElement) {
          if (getComputedStyle(ancestor).opacity === '0') return;
        }
      }
      state.visibilityBlocked = false;
      waiting.delete(node);
      visibility.unobserve(node);
      start();
    });
  }) : null;
  const dirtyRoots = new Set();
  // Ignore only our own font binding changes, never other inline declarations.
  const styleProbe = document.createElement('span').style;
  const requests = new Map();
  const pending = new Map();
  const timeout = 3500;
  let commonPromise, corePromise, batchTimer, scanTimer, familyId = 0;

  function bounded(work, onTimeout) {
    let timer;
    return Promise.race([
      work,
      new Promise((_, reject) => { timer = setTimeout(() => { onTimeout?.(); reject(new Error('Symbol timeout')); }, timeout); })
    ]).finally(() => clearTimeout(timer));
  }

  async function face(family, url) {
    // Per-consumer placeholders hide ligatures until this face has loaded.
    const font = new FontFace(family, `url("${url}")`, { style: 'normal', weight: '100 700', display: 'swap' });
    await font.load();
    document.fonts.add(font);
    return family;
  }

  async function subset(names) {
    const controller = new AbortController();
    const family = `SugarSymbolsExtra${++familyId}`;
    return bounded((async () => {
      const url = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL@20..48,100..700,0..1&icon_names=' + names.join(',') + '&display=swap';
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Symbol subset unavailable');
      const css = await response.text();
      // Accept only a variable WOFF2 subset. Never install provider CSS or a full-font fallback.
      const source = css.match(/src:\s*url\((https:\/\/fonts\.gstatic\.com\/[^)\s]+)\)\s*format\(['"]woff2['"]\)/);
      if (!source || !/font-weight:\s*100\s+700/.test(css)) throw new Error('Invalid symbol subset');
      return face(family, source[1]);
    })(), () => controller.abort());
  }

  function flush() {
    batchTimer = null;
    const entries = [...pending.entries()].sort(([a], [b]) => a.localeCompare(b));
    pending.clear();
    // Keep URL lengths bounded even when an editor inserts many choices together.
    for (let offset = 0; offset < entries.length; offset += 32) {
      const batch = entries.slice(offset, offset + 32);
      subset(batch.map(([name]) => name)).then(
        family => batch.forEach(([, resolve]) => resolve(family)),
        () => {
          // An invalid configured name must not poison the other names in its batch.
          if (batch.length === 1) batch[0][1](null);
          else batch.forEach(([name, resolve]) => subset([name]).then(resolve, () => resolve(null)));
        }
      );
    }
  }

  function load(name) {
    if (!document.fonts || !window.FontFace || !/^[a-z0-9_]{1,80}$/.test(name)) return Promise.resolve(null);
    if (common.has(name) && script.dataset.commonUrl) {
      if (!commonPromise) commonPromise = bounded(face('SugarSymbolsCommon', script.dataset.commonUrl)).catch(() => null);
      return commonPromise;
    }
    if (core.has(name)) {
      if (!corePromise) corePromise = bounded(face('SugarSymbolsCore', script.dataset.coreUrl)).catch(() => null);
      return corePromise;
    }
    if (!requests.has(name)) {
      requests.set(name, new Promise(resolve => pending.set(name, resolve)));
      if (!batchTimer) batchTimer = setTimeout(flush, 0);
    }
    return requests.get(name);
  }

  function consumer(node) {
    const pseudo = node.matches('.subscription-perks__icon--global') ? '::before'
      : node.matches('.quiz-answer__indicator--multi-checkmark') ? '::after' : null;
    const css = getComputedStyle(node, pseudo);
    const name = pseudo ? (/^["'](.*)["']$/.exec(css.content)?.[1] || '') : node.textContent.trim();
    return { name, signature: [pseudo, name, css.fontVariationSettings, css.fontWeight, css.fontSize, css.fontOpticalSizing].join('|') };
  }

  function clear(node) {
    waiting.delete(node);
    visibility?.unobserve(node);
    nodes.delete(node);
    delete node.dataset.sugarSymbolState;
    node.style.removeProperty('--sugar-symbol-font');
  }

  function scan() {
    scanTimer = null;
    const roots = [...dirtyRoots];
    dirtyRoots.clear();
    const candidates = new Set();
    roots.forEach(root => {
      if (root !== document && !root.isConnected) return;
      if (root.matches?.(selector) || nodes.has(root)) candidates.add(root);
      root.querySelectorAll?.(selector).forEach(node => candidates.add(node));
    });
    candidates.forEach(node => {
      if (!node.matches(selector)) { clear(node); return; }
      const { name, signature } = consumer(node);
      // Emoji/image global perks have no generated ligature to load.
      if (!name) { if (nodes.has(node)) clear(node); return; }
      // Each face retains the complete supported axis ranges, so an axis edit
      // revalidates this node without downloading an identical font again.
      const previous = nodes.get(node);
      if (previous?.signature === signature) {
        // Only a visibility-blocked entry needs rearming (IO v1 misses opacity
        // reveals). Geometry-only animation must keep its pending observation.
        if (waiting.has(node) && previous.visibilityBlocked) { visibility.unobserve(node); visibility.observe(node); }
        return;
      }
      const state = { signature, generation: (previous?.generation || 0) + 1 };
      nodes.set(node, state);
      if (!node.hasAttribute('aria-label') && !node.hasAttribute('role') && !node.hasAttribute('title')) node.setAttribute('aria-hidden', 'true');
      node.dataset.sugarSymbolState = 'loading';
      node.style.removeProperty('--sugar-symbol-font');
      const start = () => {
        // A queued mutation may precede its scan. Never request the old name.
        for (const root of dirtyRoots) {
          if (!root.contains(node)) continue;
          // A moving ancestor is dirty every frame without changing its glyph.
          if (consumer(node).signature !== signature) { state.signature = null; schedule(node); return; }
          break;
        }
        bounded(load(name)).catch(() => null).then(family => {
          if (nodes.get(node) !== state) return;
          if (!node.isConnected || !node.matches(selector)) { state.signature = null; return; }
          // Attribute/text mutations are delivered before promise completion, but
          // verify computed axes too (stylesheet edits can also change them).
          if (consumer(node).signature !== signature) { state.signature = null; schedule(node); return; }
          if (family) node.style.setProperty('--sugar-symbol-font', `"${family}"`);
          node.dataset.sugarSymbolState = family ? 'ready' : 'fallback';
        });
      };
      if (visibility) { waiting.set(node, start); visibility.unobserve(node); visibility.observe(node); }
      else start();
    });
  }

  function schedule(root = document) {
    if (!root) return;
    if (root.nodeType === 3) root = root.parentElement;
    if (!root || (root !== document && !root.isConnected)) return;
    // Keep the smallest set of non-overlapping roots in a single task.
    for (const queued of dirtyRoots) if (queued === root || queued.contains(root)) return;
    for (const queued of dirtyRoots) if (root.contains(queued)) dirtyRoots.delete(queued);
    dirtyRoots.add(root);
    if (!scanTimer) scanTimer = setTimeout(scan, 0);
  }

  function authorStyle(value) {
    styleProbe.cssText = value || '';
    styleProbe.removeProperty('--sugar-symbol-font');
    return styleProbe.cssText;
  }

  function stylesheet(node) {
    return node.nodeType === 1 && (node.matches('style, link[rel~="stylesheet"]') || node.querySelector('style, link[rel~="stylesheet"]'));
  }

  new MutationObserver(records => {
    records.forEach(record => {
      const target = record.target;
      // Removed subtrees may never intersect. Release IO's strong references
      // and invalidate pending completions, including moves/reinsertions.
      (record.removedNodes || []).forEach(root => {
        if (root.nodeType !== 1) return;
        if (nodes.has(root)) clear(root);
        root.querySelectorAll(selector).forEach(clear);
      });
      // CSS text edits, stylesheet insertion/removal and link configuration can
      // change any consumer, including those outside the changed subtree.
      if (target.parentElement?.closest('style') || target.matches?.('style, link') ||
          [...(record.addedNodes || []), ...(record.removedNodes || [])].some(stylesheet)) {
        schedule();
        return;
      }
      if (record.type === 'attributes') {
        // A full scan cannot rediscover a consumer whose class was removed.
        if (nodes.has(target) && !target.matches(selector)) clear(target);
        if (record.attributeName === 'style' && authorStyle(record.oldValue) === authorStyle(target.getAttribute('style'))) return;
        schedule(target);
      } else if (record.type === 'characterData') {
        schedule(target.parentElement?.closest(selector));
      } else {
        // Replacing an icon's text changes the consumer itself. Inserting an
        // unrelated child into body must not turn into another whole-page scan.
        schedule(target.closest?.(selector));
        record.addedNodes.forEach(node => { if (node.nodeType === 1) schedule(node); });
      }
    });
  }).observe(document.documentElement, {
    subtree: true, childList: true, characterData: true,
    attributes: true, attributeOldValue: true,
    attributeFilter: ['class', 'style', 'data-icon', 'data-icon-name', 'data-fill', 'data-weight', 'data-opsz', 'href', 'rel', 'media', 'disabled']
  });
  // Responsive/stylesheet changes can change inherited axis values.
  window.addEventListener('resize', () => schedule(), { passive: true });
  document.addEventListener('load', event => { if (event.target.matches?.('style, link[rel~="stylesheet"]')) schedule(); }, true);
  // CSS-only reveals do not necessarily change IO v1 geometry or the DOM.
  ['transitionstart', 'transitionend', 'animationstart', 'animationend', 'pointerover', 'focusin'].forEach(type => {
    document.addEventListener(type, event => { if (waiting.size) schedule(event.target); }, true);
  });
  dirtyRoots.add(document);
  scan();
})();
